'use client';

import { LazyTabLabECharts } from '@/components/ui/charts/lazy-tab-lab-echarts';
import type { EChartsOption } from 'echarts';

export interface ScatterPlotProps {
  data: [number, number][];
  xLabel?: string;
  yLabel?: string;
  title?: string;
  height?: number;
  trendLine?: {
    data: [number, number][];
    color?: string;
    width?: number;
  };
}

export function ScatterPlot({
  data,
  xLabel = 'X',
  yLabel = 'Y',
  title = 'Scatter Plot',
  height = 300,
  trendLine
}: ScatterPlotProps) {
  // Calculate axis ranges
  const xValues = data.map(point => point[0]);
  const yValues = data.map(point => point[1]);
  const xMin = Math.min(...xValues);
  const xMax = Math.max(...xValues);
  const yMin = Math.min(...yValues);
  const yMax = Math.max(...yValues);

  // Add 10% padding to axis ranges
  const xPadding = (xMax - xMin) * 0.1;
  const yPadding = (yMax - yMin) * 0.1;

  const chartOptions: EChartsOption = {
    title: {
      text: title,
      left: 'center'
    },
    tooltip: {
      trigger: 'axis',
      axisPointer: {
        type: 'cross'
      },
      formatter: function (params: any) {
        const scatterPoint = params.find((p: any) => p.seriesType === 'scatter');
        if (!scatterPoint) return '';
        
        const x = scatterPoint.data[0];
        const y = scatterPoint.data[1];
        return [
          `${xLabel}: ${x.toFixed(2)}`,
          `${yLabel}: ${y.toFixed(2)}`
        ].join('<br/>');
      }
    },
    grid: {
      left: '10%',
      right: '10%'
    },
    xAxis: {
      type: 'value',
      name: xLabel,
      nameLocation: 'middle',
      nameGap: 30,
      min: xMin - xPadding,
      max: xMax + xPadding,
      axisPointer: {
        show: true,
        snap: true
      }
    },
    yAxis: {
      type: 'value',
      name: yLabel,
      nameLocation: 'middle',
      nameGap: 45,
      min: yMin - yPadding,
      max: yMax + yPadding,
      axisPointer: {
        show: true,
        snap: true
      }
    },
    series: [
      {
        type: 'scatter',
        data,
        symbolSize: 8,
        emphasis: {
          scale: 1.5,
          itemStyle: {
            shadowBlur: 10,
            shadowColor: 'rgba(0, 0, 0, 0.3)'
          }
        }
      },
      ...(trendLine
        ? [
            {
              type: 'line' as const,
              data: trendLine.data,
              showSymbol: false,
              lineStyle: {
                color: trendLine.color ?? '#4d79ff',
                width: trendLine.width ?? 2
              }
            }
          ]
        : [])
    ]
  };

  return (
    <div style={{ height: `${height}px` }}>
      <LazyTabLabECharts option={chartOptions} />
    </div>
  );
} 